import {
    QueryClient,
    type QueryKey,
    type UseQueryOptions,
    type UseQueryResult,
    useQueries,
    useQuery
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { useSearchParams } from "react-router-dom"

import { groupSessionSummaryConfigAdapter } from "@/3514/adapters"
import type { IGroupSessionSummaryConfigDto, IGroupSessionSummaryConfigModel } from "@/3514/types"
import { hours, minutes } from "@/3514/utils"

import { ECoachGroupQueryKey as EQueryKey } from "./coach.group.api.config"
import adapters from "./coach.group.api.queries.adapters"
import fetchers from "./coach.group.api.queries.fetchers"
import type {
    ICoachGroupSessionParticipantProgressDto as IIndividualProgressDto,
    ICoachGroupSessionSummaryDto as ISummaryDto,
    ICoachGroupSessionSummaryModel as ISummaryModel,
    ICoachGroupSessionParticipantProgressModel as IUserParticipantModel
} from "./coach.group.api.types"

type TParams = {
    summary?: Pick<UseQueryOptions, "enabled" | "refetchInterval"> & {
        placeholderData?: ISummaryDto
    }
    individual?: Pick<UseQueryOptions, "enabled" | "refetchInterval"> & {
        individualId: number
        placeholderData?: IIndividualProgressDto
    }
}

type TSessionSummaryConfigQuery = UseQueryResult<IGroupSessionSummaryConfigModel, AxiosError>
type TSessionSummaryQuery = UseQueryResult<ISummaryModel, AxiosError>
type TSessionIndividual = UseQueryResult<IUserParticipantModel, AxiosError>

function useHook({
    summary: summaryParams = { enabled: false, placeholderData: undefined },
    individual: individualParams = {
        enabled: false,
        individualId: Number(0),
        placeholderData: undefined
    }
}: TParams): ReturnType<(params: TParams) => { summary: TSessionSummaryQuery; individual: TSessionIndividual }> {
    const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

    const sessionId: number = Number(searchParams.get("sessionId"))

    const restQueries: [TSessionSummaryQuery, TSessionIndividual] = useQueries<
        [TSessionSummaryQuery, TSessionIndividual]
    >({
        queries: [
            {
                queryKey: EQueryKey.SessionSummary({ sessionId }),
                queryFn: fetchers.summary,
                select: adapters.summary,
                staleTime: 0,
                gcTime: 0,
                enabled: summaryParams.enabled,
                notifyOnChangeProps: ["data", "isStale", "isFetched", "dataUpdatedAt"],
                throwOnError: false,
                refetchInterval: summaryParams.refetchInterval,
                ...(summaryParams.placeholderData && {
                    placeholderData: (): ISummaryDto => summaryParams.placeholderData
                })
            },
            {
                queryKey: EQueryKey.SessionIndividual({ sessionId, individualId: individualParams.individualId }),
                queryFn: fetchers.individualProgress,
                select: adapters.individualProgress,
                staleTime: minutes(30),
                gcTime: 0,
                refetchOnMount: "always",
                enabled: individualParams.enabled,
                notifyOnChangeProps: ["data", "isFetching", "isFetched", "isError", "isPlaceholderData"],
                throwOnError: false,
                refetchInterval: individualParams.refetchInterval,
                ...(individualParams.placeholderData && {
                    placeholderData: (): IIndividualProgressDto => individualParams.placeholderData
                })
            }
        ]
    })

    return { summary: restQueries[0], individual: restQueries[1] }
}

function useCoachGroupSessionSummaryConfig(): TSessionSummaryConfigQuery {
    const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

    const sessionId: number = Number(searchParams.get("sessionId"))

    return useQuery<IGroupSessionSummaryConfigDto, AxiosError, IGroupSessionSummaryConfigModel, QueryKey>({
        queryKey: EQueryKey.SessionSummaryConfig({ sessionId }),
        queryFn: fetchers.sessionSummaryConfig,
        select: groupSessionSummaryConfigAdapter,
        staleTime: hours(0.5),
        gcTime: minutes(15),
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        notifyOnChangeProps: ["data", "isError"],
        networkMode: "online",
        enabled: !!sessionId,
        experimental_prefetchInRender: true,
        throwOnError: false
    })
}

async function coachGroupSessionSummaryConfigPrefetcher(
    { qc }: { qc: QueryClient },
    { sessionId }: { sessionId: number }
): Promise<void> {
    await qc.prefetchQuery<IGroupSessionSummaryConfigDto, AxiosError, IGroupSessionSummaryConfigModel, QueryKey>({
        queryKey: EQueryKey.SessionSummaryConfig({ sessionId }),
        queryFn: fetchers.sessionSummaryConfig,
        staleTime: hours(0.5),
        gcTime: minutes(15),
        retry: false
    })
}

export {
    useHook as useCoachGroupSessionQueries,
    useCoachGroupSessionSummaryConfig,
    coachGroupSessionSummaryConfigPrefetcher
}
